import React from "react";
import { useProject } from "../../state/ProjectContext";
import EditableTable from "./_EditableTable";

export default function ValueRealization() {
  const { doc } = useProject();
  const kpis = doc?.valueRealization?.kpis || [];
  const totals = kpis.reduce((acc, k) => {
    const b = Number(k.baseline) || 0;
    const t = Number(k.target) || 0;
    const a = Number(k.actual) || 0;
    acc.tracked += 1;
    if (t !== b) {
      const denom = Math.abs(t - b) || 1;
      const closed = (a - b) / (t - b);
      acc.progressSum += Math.max(0, Math.min(1, closed));
      acc.progressCount += 1;
    }
    return acc;
  }, { tracked: 0, progressSum: 0, progressCount: 0 });
  const avgProgress = totals.progressCount ? Math.round(totals.progressSum * 100 / totals.progressCount) : 0;

  return (
    <>
      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 section-eyebrow">Value Realization</div>
        <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-zinc-900 mt-1">Value Realization</h2>
        <p className="text-sm text-zinc-500 mt-1 max-w-3xl">Living business-case tracker · baseline vs target vs actual · what most PMs skip and most clients complain about.</p>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5" data-export-capture="value-realization-kpis" data-export-title="Value Realization · Snapshot">
        <div className="card p-4"><div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">KPIs tracked</div><div className="font-display text-2xl font-extrabold text-zinc-900 mt-1">{totals.tracked}</div></div>
        <div className="card p-4"><div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">Avg. benefit closure</div><div className={`font-display text-2xl font-extrabold mt-1 ${avgProgress >= 80 ? "text-green-700" : avgProgress >= 50 ? "text-amber-700" : "text-red-700"}`}>{avgProgress}%</div><div className="text-[10px] text-zinc-500">baseline → target</div></div>
        <div className="card p-4"><div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">On track</div><div className="font-display text-2xl font-extrabold text-green-700 mt-1">{kpis.filter(k => k.status === "On track").length}</div></div>
      </div>

      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-2">Business KPIs · baseline · target · actual</div>
        <EditableTable
          sectionKey="valueRealization"
          listKey="kpis"
          testidPrefix="vr"
          labelSingular="KPI"
          columns={[
            { key: "kpi", label: "KPI", width: "200px" },
            { key: "category", label: "Category", type: "select", options: ["Cost", "Revenue", "Productivity", "Cycle time", "Quality", "Compliance", "Customer"], width: "130px" },
            { key: "unit", label: "Unit", width: "90px" },
            { key: "baseline", label: "Baseline", type: "number", width: "100px" },
            { key: "target", label: "Target", type: "number", width: "100px" },
            { key: "actual", label: "Actual", type: "number", width: "100px" },
            { key: "measureDate", label: "Measured", type: "date", width: "140px" },
            { key: "owner", label: "Owner", width: "120px" },
            { key: "status", label: "Status", type: "select", options: ["Not measured", "At risk", "On track", "Achieved", "Exceeded"], width: "130px" },
          ]}
          emptyRow={() => ({ kpi: "", category: "Cost", unit: "$K", baseline: 0, target: 0, actual: 0, measureDate: new Date().toISOString().split("T")[0], owner: "", status: "Not measured" })}
        />
      </div>

      {/* Progress bars visualization */}
      {kpis.length > 0 && (
        <div className="card p-5" data-export-capture="value-realization-bars" data-export-title="Value Realization · KPI Progress">
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-3">Benefit closure by KPI</div>
          <div className="space-y-2.5">
            {kpis.map((k, i) => {
              const b = Number(k.baseline) || 0;
              const t = Number(k.target) || 0;
              const a = Number(k.actual) || 0;
              const pct = t !== b ? Math.max(0, Math.min(100, Math.round(((a - b) / (t - b)) * 100))) : 0;
              return (
                <div key={i} className="grid grid-cols-[220px_1fr_80px] gap-3 items-center text-xs">
                  <div className="truncate">{k.kpi || `KPI ${i + 1}`}</div>
                  <div className="relative h-3 bg-zinc-100 rounded-sm overflow-hidden">
                    <div className="h-full" style={{ width: `${pct}%`, background: pct >= 80 ? "#16A34A" : pct >= 50 ? "#F59E0B" : "#DC2626" }}/>
                  </div>
                  <div className="text-right font-mono text-[10px] text-zinc-500">{pct}%</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
